import React, { useEffect, useState, useMemo } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Dimensions,
  SafeAreaView,
  Linking,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";

const { width } = Dimensions.get("window");

// --- Helper: followers string to 10k-range (handles k/m/b and plain numbers) ---
const parseFollowerRange = (followersString) => {
  if (!followersString) return "0–10k";

  const s = String(followersString).trim().toLowerCase();
  const match = s.match(/([\d.,]+)\s*([kmb]?)/i);
  if (!match) return "0–10k";

  const base = parseFloat(match[1].replace(/,/g, ""));
  if (isNaN(base)) return "0–10k";

  const suffix = match[2];
  const multiplier =
    suffix === "k"
      ? 1000
      : suffix === "m"
      ? 1000000
      : suffix === "b"
      ? 1000000000
      : 1;

  const num = Math.round(base * multiplier);

  const ranges = [];
  for (let i = 0; i < 1000000; i += 10000) {
    ranges.push({
      max: i + 10000,
      label: `${i / 1000}k–${(i + 10000) / 1000}k`,
    });
  }
  const range = ranges.find((r) => num <= r.max);
  return range ? range.label : "1M+";
};

// --- Format date/time ---
const formatNiceDateTime = (iso) => {
  if (!iso) return "a date soon";
  const d = new Date(iso);
  const day = d.getDate();
  const month = d.toLocaleString(undefined, { month: "long" });
  const hours = d.getHours() % 12 || 12;
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  const ord = (n) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  return `${day}${ord(day)} ${month} ${hours}:${minutes}${ampm}`;
};

const CreatorPreview = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { Promoter, CollabID } = route.params || {};

const [creator, setCreator] = useState(null);
const [booking, setBooking] = useState(null);
const [loading, setLoading] = useState(true);
const [bookingLoading, setBookingLoading] = useState(true);
const [countdown, setCountdown] = useState("");
const [creatorRevealLoading, setCreatorRevealLoading] = useState(false);
const [creatorRevealError, setCreatorRevealError] = useState("");


  // Fetch creator info
  useEffect(() => {
    if (!Promoter) return;
    const url = `https://collabbayadmin.com/APIControllers/GetCreatorPreviewData.php?creator=${encodeURIComponent(
      Promoter
    )}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => setCreator(data))
      .catch((err) => console.error("Preview fetch error:", err))
      .finally(() => setLoading(false));
  }, [Promoter]);

  // Fetch booking info
  useEffect(() => {
    if (!CollabID) {
      setBookingLoading(false);
      return;
    }
    const url = `https://collabbayadmin.com/APIControllers/GetPreviewBookingDetails.php?collabid=${encodeURIComponent(
      CollabID
    )}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => setBooking(data))
      .catch((err) => console.error("Booking preview fetch error:", err))
      .finally(() => setBookingLoading(false));
  }, [CollabID]);

  // Countdown timer (live updating)
  useEffect(() => {
    if (!booking?.CreditAvailableStart) return;
    const target = new Date(booking.CreditAvailableStart).getTime();

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) {
        setCountdown("0d 0h 0m 0s");
        clearInterval(interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    }, 1000);

    return () => clearInterval(interval);
  }, [booking?.CreditAvailableStart]);

  const followerRange = useMemo(
    () => parseFollowerRange(creator?.PlatformFollowers),
    [creator?.PlatformFollowers]
  );

  const niceWhen = bookingLoading
    ? "..."
    : formatNiceDateTime(booking?.CreditAvailableStart);

  if (loading)
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#000" />
      </View>
    );

  if (!creator || creator.error)
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>Creator not found.</Text>
      </View>
    );

  const firstLetter = Promoter ? Promoter.charAt(0) : "C";
  const blurred = "•••••••";
  const turnaround = booking?.TurnAroundDays || "a few";


  const canClickRevealCreator = booking?.CreatorInstagramEligible === true;

const handleRevealCreator = async () => {
  if (!CollabID || creatorRevealLoading) return;

  setCreatorRevealLoading(true);
  setCreatorRevealError("");

  try {
    const url = `https://collabbayadmin.com/APIControllers/GetPreviewBookingDetails.php?collabid=${encodeURIComponent(
      CollabID
    )}&revealcreator=1`;

    const res = await fetch(url);
    const data = await res.json();

    if (data?.CreatorInstagramAllowed === true && data?.CreatorInstagramUrl) {
      Linking.openURL(data.CreatorInstagramUrl);
    } else {
      setCreatorRevealError(
        data?.CreatorInstagramMessage || "Creator preview is not available."
      );
    }
  } catch (err) {
    console.error("Reveal creator Instagram error:", err);
    setCreatorRevealError("Could not open creator preview. Please try again.");
  } finally {
    setCreatorRevealLoading(false);
  }
};

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
      >
        {/* 🔙 Back */}
        <View style={styles.backContainer}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={22} color="#000" />
          </TouchableOpacity>
        </View>

        {/* 🧾 Main Card */}
        <View style={styles.card}>
          {/* 🖼️ Image + overlays */}
          <View style={styles.imageWrapper}>
            <Image
              source={{ uri: creator.ProfileImage }}
              style={styles.profileImage}
            />

            {/* Followers bottom-left */}
            <View style={styles.overlayLeft}>
              <View style={styles.overlayBox}>
                <Text style={styles.overlayBigText}>{followerRange}</Text>
                <Text style={styles.overlaySmallText}>Followers</Text>
              </View>
            </View>

            {/* Age bottom-right */}
            {creator.Age && (
              <View style={styles.overlayRight}>
                <View style={styles.overlayBox}>
                  <Text style={styles.overlayBigText}>{creator.Age}</Text>
                  <Text style={styles.overlaySmallText}>Age</Text>
                </View>
              </View>
            )}
          </View>

{/* @Username */}
<View style={styles.middleSection}>
  <View style={styles.creatorNameRow}>
    <Text style={styles.username}>
      @{firstLetter}
      <Text style={styles.blurredText}>{blurred}</Text>
    </Text>

    <Ionicons
      name="checkmark-circle"
      size={18}
      color="#1DA1F2"
      style={styles.verifiedTick}
    />
  </View>

  {canClickRevealCreator ? (
    <TouchableOpacity
      style={styles.creatorRevealButton}
      onPress={handleRevealCreator}
      activeOpacity={0.88}
      disabled={creatorRevealLoading}
    >
<Ionicons name="eye-outline" size={17} color="#fff" style={{ marginRight: 7 }} />

      <Text style={styles.creatorRevealTitle}>
        {creatorRevealLoading ? "Opening preview..." : "Preview creator before accepting?"}
      </Text>

<Ionicons
  name="arrow-forward"
  size={14}
  color="white"
  style={{ marginLeft: 8 }}
/>

    </TouchableOpacity>
  ) : null}

  {creatorRevealError ? (
    <Text style={styles.creatorRevealError}>{creatorRevealError}</Text>
  ) : null}
</View>


          {/* Tag below image */}
          {creator.ContentCategories && (
            <View style={styles.tagBox}>
              <Text style={styles.tagText}>
                {creator.ContentCategories} Creator
              </Text>
            </View>
          )}

          {/* Social proof */}
          {creator.VenueCount > 0 && (
            <View style={styles.acceptedBox}>
              <Ionicons
                name="checkmark-circle"
                size={18}
                color="green"
                style={{ marginRight: 6 }}
              />
              <Text style={styles.acceptedText}>
                <Text style={{ fontWeight: "700" }}>
                  {creator.VenueCount}
                </Text>{" "}
                other venues using collabbay {"\n"}have accepted this creator
              </Text>
            </View>
          )}

          {/* Date + Countdown */}
          <View style={styles.middleSection}>
            <Text style={styles.visitSmall}>
              For {booking?.BrandName || "your venue"} on
            </Text>
            <Text style={styles.visitDate}>{niceWhen}</Text>
            {countdown ? (
              <Text style={styles.countdownText}>{countdown}</Text>
            ) : null}
          </View>

          {/* Exchange info box */}
          <View style={styles.infoBox}>
            <Ionicons
              name="film-outline"
              size={18}
              color="#000"
              style={{ marginRight: 6 }}
            />
            <Text style={styles.infoText}>
              This creator could visit, promote {booking?.BrandName || "your venue"} and deliver
              their reel within {turnaround} day
              {turnaround === "1" ? "" : "s"}. If this is a proposal, not a request- the creator will also need to confirm the collaboration.
            </Text>
          </View>

     {/* CTA */}
<TouchableOpacity
  style={styles.primaryButton}
  onPress={() =>
    Linking.openURL("https://collabbayadmin.com/subscribe")
  }
>
  <Text style={styles.primaryText}>Unlock and review booking</Text>
<Ionicons
  name="arrow-forward"
  size={18}
  color="#black"
  style={{ marginLeft: 8 }}
/>
</TouchableOpacity>

<View style={styles.secureCollabsBox}>
  <Ionicons name="shield-checkmark-outline" size={14} color="#666" />
  <Text style={styles.secureCollabsText}>Secure collabs with collabbay™</Text>
</View>

{CollabID && (
  <Text style={styles.collabRef}>Ref: Collab #{CollabID}</Text>
)}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

// ---------- STYLES ----------
const IMG_BORDER = 40;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#fff",
    alignItems: "center",
  },
  container: {
    alignItems: "center",
    backgroundColor: "#fff",
    paddingBottom: 40,
    width: "100%",
    maxWidth: 700,
    alignSelf: "center",
  },
  center: { flex: 1, justifyContent: "center", alignItems: "center" },
  errorText: { color: "red" },
  backContainer: {
    width: "100%",
    paddingHorizontal: 20,
    paddingTop: 20,
    alignItems: "flex-start",
  },
  backButton: {
    backgroundColor: "#f3f3f3",
    borderRadius: 30,
    padding: 8,
  },
  card: {
    width: "95%",
    borderRadius: 26,
    backgroundColor: "#fff",
    marginTop: 10,
    alignItems: "center",
    paddingBottom: 26,
    borderWidth: 1,
    borderColor: "#eee",
  },
  imageWrapper: {
    position: "relative",
    width: "80%",
    alignSelf: "center",
    borderRadius: IMG_BORDER,
    overflow: "hidden",
    marginTop: 10,
  },
  profileImage: {
    width: "100%",
    height: 270,
    resizeMode: "cover",
    borderRadius: IMG_BORDER,
  },
  overlayLeft: { position: "absolute", bottom: 18, left: 18 },
  overlayRight: { position: "absolute", bottom: 18, right: 18 },
  overlayBox: {
    backgroundColor: "rgba(0,0,0,0.35)",
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  overlayBigText: {
    color: "#fff",
    fontWeight: "900",
    fontSize: 20,
  },
  overlaySmallText: {
    color: "#fff",
    fontSize: 11,
    opacity: 0.9,
  },
  tagBox: {
    marginTop: 10,
    backgroundColor: "#f3f3f3",
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 20,
  },
  tagText: { fontSize: 14, color: "#000", fontWeight: "600" },
  acceptedBox: {
    marginTop: 12,
    backgroundColor: "#fafafa",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    flexDirection: "row",
    alignItems: "center",
    maxWidth: "85%",
  },
  acceptedText: {
    fontSize: 14,
    color: "#000",
    textAlign: "center",
    lineHeight: 18,
  },
  middleSection: { marginTop: 20, alignItems: "center" },
  username: {
    fontSize: 20,
    fontWeight: "500",
    color: "#000",
    marginBottom: 6,
    letterSpacing: 0.3,
  },
  blurredText: {
    color: "transparent",
    textShadowColor: "rgba(0,0,0,0.3)",
    textShadowRadius: 6,
  },
  creatorNameRow: {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 4,
},
verifiedTick: {
  marginLeft: 6,
  marginBottom: 5,
},creatorRevealButton: {
  marginTop: 4,
  marginBottom: 8,
  backgroundColor: "#1DA1F2",
  borderRadius: 999,
  paddingVertical: 8,
  paddingHorizontal: 14,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "center",
  borderWidth: 1,
  borderColor: "#1DA1F2",
},
creatorRevealTitle: {
  color: "#fff",
  fontSize: 13,
  fontWeight: "800",
},
creatorRevealError: {
  marginTop: 7,
  fontSize: 12,
  color: "#777",
  textAlign: "center",
  fontWeight: "500",
},
  visitSmall: {
    color: "#777",
    fontSize: 15,
    marginTop: 4,
    textAlign: "center",
  },
  visitDate: {
    fontSize: 26,
    fontWeight: "800",
    color: "#000",
    textAlign: "center",
    marginTop: 6,
  },
  countdownText: {
    marginTop: 4,
    fontSize: 15,
    fontWeight: "500",
    color: "#777", // subtle grey
    textAlign: "center",
  },
  infoBox: {
    marginTop: 18,
    backgroundColor: "#f9f9f9",
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    width: "85%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  infoText: {
    color: "#000",
    fontSize: 14,
    textAlign: "center",
    flexShrink: 1,
    lineHeight: 20,
  },
primaryButton: {
  width: "85%",
  backgroundColor: "#1DA1F2",
  paddingVertical: 16,
  borderRadius: 12,
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "row",
  marginTop: 22,
  borderWidth: 1,
  borderColor: "#1DA1F2",
},
primaryText: {
  color: "#000",
  fontWeight: "900",
  fontSize: 16,
},
secureCollabsBox: {
  marginTop: 10,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
},
secureCollabsText: {
  marginLeft: 5,
  color: "#666",
  fontSize: 12,
  fontWeight: "600",
},
collabRef: {
  color: "#aaa",
  fontSize: 12,
  marginTop: 8,
},
});

export default CreatorPreview;